import React from 'react';
import PropTypes from 'prop-types';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import { Box, Modal, Typography } from '@mui/material';

import WaeButton, { BUTTON_TYPE, BUTTON_VARIANT } from '../../../../../../../components/Button';
import { BLACK, PRIMARY_PURPLE } from '../../../../../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../../../../../theme/fontConstants';
import { getReviewStatusLabelKey } from '../documentReview';

const modalRootSx = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: '420px' },
  bgcolor: BLACK[0],
  borderRadius: '16px',
  boxShadow: 24,
  p: '30px 38px',
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

const titleSx = {
  fontSize: '20px',
  fontFamily: PRIMARY_FONT[700],
  color: BLACK[100],
  lineHeight: '24px',
};

const bodySx = {
  fontFamily: PRIMARY_FONT[400],
  fontSize: '16px',
  color: BLACK[70],
  lineHeight: '22px',
};

const statusEmphasisSx = {
  fontFamily: PRIMARY_FONT[700],
  color: BLACK[100],
};

const footerSx = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 1.5,
  mt: 1,
};

const pillButtonSx = {
  borderRadius: '100px',
  fontFamily: PRIMARY_FONT[500],
  fontSize: '14px',
  px: 3,
  py: 1,
  boxShadow: 'none',
  minHeight: '36px',
  height: 'auto',
};

const cancelButtonSx = {
  ...pillButtonSx,
  color: BLACK[100],
  border: `1px solid ${BLACK[30]}`,
  '&:hover': {
    backgroundColor: BLACK[10],
    boxShadow: 'none',
    border: `1px solid ${BLACK[30]}`,
  },
};

const confirmButtonSx = {
  ...pillButtonSx,
  color: BLACK[0],
  '&:hover': {
    backgroundColor: PRIMARY_PURPLE[60],
    boxShadow: 'none',
  },
};

const DocumentStatusChangeConfirmationModal = ({
  open,
  status,
  isConfirming,
  onCancel,
  onConfirm,
}) => {
  const statusLabelKey = getReviewStatusLabelKey(status);

  return (
    <Modal
      open={open}
      onClose={isConfirming ? undefined : onCancel}
      aria-labelledby="document-status-change-confirmation-title"
      aria-describedby="document-status-change-confirmation-description"
      sx={{ zIndex: 1400 }}
    >
      <Box sx={modalRootSx}>
        <Typography id="document-status-change-confirmation-title" sx={titleSx}>
          {LanguageConverterV2('userDetail.employeeDocumentation.statusChange.title')}
        </Typography>
        <Typography id="document-status-change-confirmation-description" sx={bodySx}>
          {LanguageConverterV2('userDetail.employeeDocumentation.statusChange.body')}
          {statusLabelKey ? ' ' : ''}
          {statusLabelKey && (
            <Box component="span" sx={statusEmphasisSx}>
              {LanguageConverterV2(statusLabelKey)}
            </Box>
          )}
        </Typography>
        <Box sx={footerSx}>
          <WaeButton
            type={BUTTON_TYPE.BUTTON}
            variant={BUTTON_VARIANT.DEFAULT}
            actionColor={BLACK[0]}
            hoverColor={BLACK[10]}
            disabled={isConfirming}
            onClick={onCancel}
            sx={cancelButtonSx}
          >
            {LanguageConverterV2('button.cancel')}
          </WaeButton>
          <WaeButton
            type={BUTTON_TYPE.BUTTON}
            variant={BUTTON_VARIANT.DEFAULT}
            actionColor={PRIMARY_PURPLE[70]}
            hoverColor={PRIMARY_PURPLE[60]}
            disabled={isConfirming}
            onClick={onConfirm}
            sx={confirmButtonSx}
          >
            {LanguageConverterV2('button.confirm')}
          </WaeButton>
        </Box>
      </Box>
    </Modal>
  );
};

DocumentStatusChangeConfirmationModal.propTypes = {
  isConfirming: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  status: PropTypes.string,
};

DocumentStatusChangeConfirmationModal.defaultProps = {
  isConfirming: false,
  status: null,
};

export default DocumentStatusChangeConfirmationModal;
